import type { ReactNode } from "react";
import { AlignLeft } from "lucide-react";
import type { FrameworkResponse } from "@patent-studio/api-types";
import { useCarriedItem } from "@/components/patterns/SortableList";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import { Thumbnail } from "@/components/ui/Thumbnail";
import { cn } from "@/lib/utils";
import { carriedId } from "./FrameworkDrag";
import { PlaceIn } from "./PlaceIn";
import type { UnplacedItem } from "./framework-edits";
import { FigureLead, type Labels } from "./SubsectionRow";

interface ItemProps {
  item: UnplacedItem;
  labels: Labels;
  sections: { id: string; title: string }[];
  readOnly: boolean;
  onPlace: (item: UnplacedItem, sectionId: string) => void;
}

/** How the tray, the drag and a reader name an unplaced item: "FIG. 3", "Claim 7". */
export function unplacedWords(item: UnplacedItem, labels: Labels): string {
  return item.kind === "figure"
    ? `FIG. ${labels.figure(item.id) ?? "?"}`
    : `Claim ${String(labels.claim(item.id) ?? "?")}`;
}

/** What the plan leaves out, figures first, then claims. */
export function unplacedOf(response: FrameworkResponse): UnplacedItem[] {
  return [
    ...response.unplaced.figures.map((id) => ({ kind: "figure" as const, id })),
    ...response.unplaced.claims.map((id) => ({ kind: "claim" as const, id })),
  ];
}

/** A view as v0.26 shows it in the tray: its first letter in capitals. */
function capitalized(view: string | undefined): string | undefined {
  return view === undefined
    ? undefined
    : view.charAt(0).toUpperCase() + view.slice(1);
}

function Row({
  item,
  labels,
  sections,
  readOnly,
  onPlace,
  drag,
}: ItemProps & {
  drag?: {
    ref: (element: Element | null) => void;
    handle: ReactNode;
    chrome: string;
  };
}) {
  const words = unplacedWords(item, labels);
  return (
    <li ref={drag?.ref} className={cn("flex items-center gap-3", drag?.chrome)}>
      {drag?.handle}
      {item.kind === "figure" ? (
        <FigureLead fileId={item.id} labels={labels} />
      ) : (
        <Thumbnail src={undefined}>
          <AlignLeft />
        </Thumbnail>
      )}
      <span className="flex min-w-0 flex-1 items-center gap-2 text-sm">
        {item.kind === "claim" && (
          <span className="font-semibold">{words}</span>
        )}
        <span className="truncate text-muted-foreground">
          {item.kind === "figure"
            ? capitalized(labels.view(item.id))
            : labels.claimTitle(item.id)}
        </span>
      </span>
      {!readOnly && (
        <PlaceIn
          words={words}
          sections={sections}
          onPlace={(sectionId) => {
            onPlace(item, sectionId);
          }}
        />
      )}
    </li>
  );
}

function CarriedRow(props: ItemProps) {
  const drag = useCarriedItem({
    id: carriedId(props.item),
    type: "unplaced",
    label: `Place ${unplacedWords(props.item, props.labels)}`,
    size: "sm",
  });
  return <Row {...props} drag={drag} />;
}

/**
 * The figures and independent claims the plan does not place, each carried onto a section or
 * placed at the end of the one chosen. Confirmed, the tray only lists them: placing a late item
 * is the notification's action.
 */
export function UnplacedTray({
  items,
  ...shared
}: Omit<ItemProps, "item"> & { items: UnplacedItem[] }) {
  return (
    <Panel tone="warning" pad="snug" className="mt-6 space-y-2 text-sm">
      <MicroLabel>{`Unplaced · ${String(items.length)}`}</MicroLabel>
      <p>
        These are in no section yet. Your ordering is kept; drag each item into
        a section or choose where it goes.
      </p>
      <ul className="space-y-1.5">
        {items.map((item) =>
          shared.readOnly ? (
            <Row key={carriedId(item)} item={item} {...shared} />
          ) : (
            <CarriedRow key={carriedId(item)} item={item} {...shared} />
          ),
        )}
      </ul>
    </Panel>
  );
}
